/**
 * Save or share a PDF blob in a way that works on desktop and iOS Safari.
 * Avoids the empty duplicate file iOS often creates with immediate revokeObjectURL.
 */
export async function downloadPdfBlob(blob, filename = "document.pdf") {
    const safeName = sanitizePdfFilename(filename);
    const pdfBlob = blob.type === "application/pdf"
        ? blob
        : new Blob([blob], { type: "application/pdf" });

    if (isAppleTouchDevice()) {
        const file = new File([pdfBlob], safeName, { type: "application/pdf" });
        if (typeof navigator.share === "function" && typeof navigator.canShare === "function") {
            try {
                if (navigator.canShare({ files: [file] })) {
                    await navigator.share({
                        files: [file],
                        title: safeName
                    });
                    return;
                }
            } catch (err) {
                // User cancelled the share sheet — do not open a second empty download.
                if (err?.name === "AbortError") {
                    return;
                }
            }
        }

        // Fallback when share is unavailable: open PDF (Safari ignores <a download> for blobs).
        const url = URL.createObjectURL(pdfBlob);
        const opened = window.open(url, "_blank", "noopener,noreferrer");
        if (!opened) {
            window.location.assign(url);
        }
        window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
        return;
    }

    const url = URL.createObjectURL(pdfBlob);
    try {
        const link = document.createElement("a");
        link.href = url;
        link.download = safeName;
        link.rel = "noopener";
        document.body.appendChild(link);
        link.click();
        link.remove();
    } finally {
        window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    }
}

function sanitizePdfFilename(name) {
    const trimmed = (name || "document.pdf").trim();
    const ascii = trimmed
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/ç/gi, "c")
        .replace(/[^a-zA-Z0-9._-]+/g, "-")
        .replace(/-+/g, "-")
        .replace(/^-|-$/g, "");
    const withExt = ascii.toLowerCase().endsWith(".pdf") ? ascii : `${ascii || "document"}.pdf`;
    return withExt;
}

function isAppleTouchDevice() {
    if (typeof navigator === "undefined") {
        return false;
    }
    return /iPad|iPhone|iPod/i.test(navigator.userAgent)
        || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}
